import { useCallback, useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  MailPlus,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  Search,
  ShieldOff,
} from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Loading, PageHeader } from "../../components/shared";

const STATUS_META = {
  ELIGIBLE: {
    label: "Eligible",
    color: "bg-emerald-50 text-emerald-800 border-emerald-200",
    icon: CheckCircle2,
  },
  PENDING: {
    label: "Pending",
    color: "bg-amber-50 text-amber-800 border-amber-200",
    icon: Clock,
  },
  ACTIVATED: {
    label: "Activated",
    color: "bg-blue-50 text-blue-800 border-blue-200",
    icon: CheckCircle2,
  },
  EXPIRED: {
    label: "Expired",
    color: "bg-orange-50 text-orange-800 border-orange-200",
    icon: AlertCircle,
  },
  REVOKED: {
    label: "Revoked",
    color: "bg-slate-100 text-slate-700 border-slate-200",
    icon: ShieldOff,
  },
  INELIGIBLE: {
    label: "Ineligible",
    color: "bg-rose-50 text-rose-800 border-rose-200",
    icon: AlertCircle,
  },
};

function StatusBadge({ status, testid }) {
  const meta = STATUS_META[status] || STATUS_META.INELIGIBLE;
  const Icon = meta.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${meta.color}`}
      data-testid={testid}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}

export default function ResidentAccess() {
  const [data, setData] = useState(null);
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [working, setWorking] = useState("");

  const loadActivity = useCallback(async () => {
    try {
      const res = await api.get("/resident-admin/invitations");
      setData(res.data);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load resident access"
      );
      setData({ invitations: [], accesses: [] });
    }
  }, []);

  const loadCandidates = useCallback(async (q = "") => {
    setCandidatesLoading(true);
    try {
      const res = await api.get("/resident-admin/invitation-candidates", {
        params: { q, limit: 25 },
      });
      setCandidates(res.data.items || []);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not search for members"
      );
    } finally {
      setCandidatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivity();
    loadCandidates("");
  }, [loadActivity, loadCandidates]);

  useEffect(() => {
    const t = setTimeout(() => loadCandidates(query), 250);
    return () => clearTimeout(t);
  }, [query, loadCandidates]);

  const invite = async (personId) => {
    setWorking(personId);
    try {
      const response = await api.post("/resident-admin/invitations", {
        person_id: personId,
      });
      toast.success(
        response.data.status === "SENT"
          ? "Invitation sent"
          : "Delivery pending"
      );
      await Promise.all([loadActivity(), loadCandidates(query)]);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create invitation"
      );
    } finally {
      setWorking("");
    }
  };

  const invitationAction = async (invitationId, action) => {
    setWorking(invitationId);
    try {
      await api.post(`/resident-admin/invitations/${invitationId}/${action}`);
      toast.success(
        action === "resend"
          ? "Invitation queued for delivery"
          : "Invitation revoked"
      );
      await Promise.all([loadActivity(), loadCandidates(query)]);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update invitation"
      );
    } finally {
      setWorking("");
    }
  };

  const accessAction = async (accessId, action) => {
    setWorking(accessId);
    try {
      await api.post(`/resident-admin/access/${accessId}/${action}`);
      toast.success(`Resident access ${action}ed`);
      await Promise.all([loadActivity(), loadCandidates(query)]);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not update resident access"
      );
    } finally {
      setWorking("");
    }
  };

  if (!data) return <Loading />;

  return (
    <div data-testid="resident-access-page">
      <PageHeader
        subtitle="Invite verified members and manage their resident portal access."
        title="Resident access"
      />

      <section
        className="rounded-lg border border-slate-200 bg-white p-5"
        data-testid="resident-invite-search-panel"
      >
        <h2 className="font-display text-lg font-semibold">Find a member</h2>
        <p className="mt-1 text-xs text-slate-600">
          Search by name, email, mobile or unit number. Members are shown with
          invitation eligibility and the reason when ineligible.
        </p>
        <label className="mt-4 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 focus-within:border-primary focus-within:bg-white">
          <Search className="h-4 w-4 text-slate-500" />
          <input
            className="w-full bg-transparent text-sm outline-none"
            data-testid="resident-invite-search-input"
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search members"
            type="search"
            value={query}
          />
        </label>

        <div
          className="mt-4 space-y-3"
          data-testid="resident-invite-candidate-list"
        >
          {candidatesLoading && (
            <p className="text-sm text-slate-500">Searching…</p>
          )}
          {!candidatesLoading && candidates.length === 0 && (
            <p
              className="border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600"
              data-testid="resident-invite-empty"
            >
              {query.trim()
                ? "No members matched your search."
                : "No members found in this society yet."}
            </p>
          )}
          {!candidatesLoading &&
            candidates.map((candidate) => {
              const eligibility = candidate.eligibility || {};
              const invitationId = candidate.invitation?.invitation_id || "";
              const accessId = candidate.access?.access_id || "";
              const busy = [
                candidate.person_id,
                invitationId,
                accessId,
              ].includes(working);
              return (
                <article
                  className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4"
                  data-testid={`resident-candidate-${candidate.person_id}`}
                  key={candidate.person_id}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-900">
                        {candidate.display_name || "—"}
                      </p>
                      {candidate.source === "FRESH_FINANCE" && (
                        <span
                          className="border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-800"
                          data-testid={`resident-candidate-${candidate.person_id}-fresh-finance-source`}
                        >
                          Units &amp; Billing
                        </span>
                      )}
                      <StatusBadge
                        status={eligibility.status || "INELIGIBLE"}
                        testid={`resident-candidate-${candidate.person_id}-status`}
                      />
                    </div>
                    <p className="mt-1 text-xs text-slate-600">
                      {candidate.email || "no email"} ·{" "}
                      {candidate.mobile || "no mobile"}
                    </p>
                    {candidate.units.length > 0 && (
                      <p className="mt-1 text-xs text-slate-500">
                        Units:{" "}
                        {candidate.units
                          .map((u) => `${u.unit_number} (${u.role})`)
                          .join(", ")}
                      </p>
                    )}
                    {eligibility.reason && (
                      <p
                        className="mt-2 text-xs text-slate-700"
                        data-testid={`resident-candidate-${candidate.person_id}-reason`}
                      >
                        {eligibility.reason}
                      </p>
                    )}
                    {eligibility.action && !eligibility.can_invite &&
                      !eligibility.can_resend &&
                      !eligibility.can_revoke && (
                        <p className="mt-1 text-xs italic text-slate-500">
                          Next step: {eligibility.action}
                        </p>
                      )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {eligibility.can_invite && (
                      <Button
                        data-testid={`resident-candidate-${candidate.person_id}-invite`}
                        disabled={busy}
                        onClick={() => invite(candidate.person_id)}
                        size="sm"
                      >
                        <MailPlus />
                        Invite
                      </Button>
                    )}
                    {eligibility.can_resend && invitationId && (
                      <Button
                        data-testid={`resident-candidate-${candidate.person_id}-resend`}
                        disabled={busy}
                        onClick={() =>
                          invitationAction(invitationId, "resend")
                        }
                        size="sm"
                        variant="outline"
                      >
                        <RefreshCw />
                        Resend
                      </Button>
                    )}
                    {eligibility.can_revoke && invitationId && (
                      <Button
                        data-testid={`resident-candidate-${candidate.person_id}-revoke-invitation`}
                        disabled={busy}
                        onClick={() =>
                          invitationAction(invitationId, "revoke")
                        }
                        size="sm"
                        variant="outline"
                      >
                        <ShieldOff />
                        Revoke invite
                      </Button>
                    )}
                    {accessId && candidate.access?.status === "ACTIVE" && (
                      <Button
                        data-testid={`resident-candidate-${candidate.person_id}-suspend`}
                        disabled={busy}
                        onClick={() => accessAction(accessId, "suspend")}
                        size="sm"
                        variant="outline"
                      >
                        <PauseCircle />
                        Suspend
                      </Button>
                    )}
                    {accessId && candidate.access?.status === "SUSPENDED" && (
                      <Button
                        data-testid={`resident-candidate-${candidate.person_id}-resume`}
                        disabled={busy}
                        onClick={() => accessAction(accessId, "resume")}
                        size="sm"
                        variant="outline"
                      >
                        <PlayCircle />
                        Resume
                      </Button>
                    )}
                  </div>
                </article>
              );
            })}
        </div>
        <p
          className="mt-4 text-xs leading-5 text-slate-600"
          data-testid="resident-invite-guidance"
        >
          Invitations expire after 72 hours and are sent from MySocietyMitra.
          A verified Society official email is used as the reply-to address when
          available. Passwords and invitation secrets are never shown here.
        </p>
      </section>

      <section className="mt-8" data-testid="resident-invitation-list">
        <h2 className="font-display text-xl font-semibold text-slate-900">
          Invitation activity
        </h2>
        <div className="mt-3 space-y-3">
          {data.invitations.length === 0 ? (
            <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
              No resident invitations yet.
            </p>
          ) : (
            data.invitations.map((invitation) => (
              <article
                className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4"
                data-testid={`resident-invitation-${invitation.invitation_id}`}
                key={invitation.invitation_id}
              >
                <div>
                  <p className="font-medium text-slate-900">
                    {invitation.person?.display_name || "Resident"}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">
                    {invitation.status}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {!["ACCEPTED", "REVOKED"].includes(invitation.status) && (
                    <Button
                      data-testid={`resident-resend-${invitation.invitation_id}`}
                      onClick={() =>
                        invitationAction(invitation.invitation_id, "resend")
                      }
                      size="sm"
                      variant="outline"
                    >
                      <RefreshCw />
                      Resend
                    </Button>
                  )}
                  {!["ACCEPTED", "REVOKED"].includes(invitation.status) && (
                    <Button
                      data-testid={`resident-revoke-invitation-${invitation.invitation_id}`}
                      onClick={() =>
                        invitationAction(invitation.invitation_id, "revoke")
                      }
                      size="sm"
                      variant="outline"
                    >
                      <ShieldOff />
                      Revoke
                    </Button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="mt-8" data-testid="resident-access-list">
        <h2 className="font-display text-xl font-semibold text-slate-900">
          Portal access
        </h2>
        <div className="mt-3 space-y-3">
          {data.accesses.length === 0 ? (
            <p className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600">
              Active resident access will appear here after an invitation is
              accepted.
            </p>
          ) : (
            data.accesses.map((access) => (
              <article
                className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4"
                data-testid={`resident-access-${access.access_id}`}
                key={access.access_id}
              >
                <div>
                  <p className="font-medium text-slate-900">
                    {access.person?.display_name || "Resident"}
                  </p>
                  <p className="mt-1 text-xs text-slate-600">{access.status}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {access.status === "ACTIVE" && (
                    <Button
                      data-testid={`resident-suspend-${access.access_id}`}
                      onClick={() => accessAction(access.access_id, "suspend")}
                      size="sm"
                      variant="outline"
                    >
                      <PauseCircle />
                      Suspend
                    </Button>
                  )}
                  {access.status === "SUSPENDED" && (
                    <Button
                      data-testid={`resident-resume-${access.access_id}`}
                      onClick={() => accessAction(access.access_id, "resume")}
                      size="sm"
                      variant="outline"
                    >
                      <PlayCircle />
                      Resume
                    </Button>
                  )}
                  {!["REVOKED", "PENDING"].includes(access.status) && (
                    <Button
                      data-testid={`resident-revoke-access-${access.access_id}`}
                      onClick={() => accessAction(access.access_id, "revoke")}
                      size="sm"
                      variant="outline"
                    >
                      <ShieldOff />
                      Revoke
                    </Button>
                  )}
                </div>
              </article>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
